<template>
  <section class="espo-provenance" aria-label="Card provenance">
    <div class="provenance-heading"><h2>Bitcoin provenance</h2><a :href="guideUrl" class="text-link" target="_blank" rel="noopener noreferrer">Numbering rules ↗</a></div>
    <p v-if="!validSerial(serial)" class="state-note">Choose a RAREPEPE serial from 1 to 300.</p>
    <p v-else-if="!status" class="state-note">{{ statusError ? 'The latest numbered history is unavailable right now.' : 'Checking the latest numbered history…' }} The saved history below remains available.</p>
    <p v-else-if="!canInspect(status)" class="state-note">{{ status?.projection_error || status?.archive_error ? 'History indexing is paused before RAREPEPE’s issuance.' : 'History indexing has not reached RAREPEPE’s issuance yet.' }} The saved history below remains available.</p>
    <template v-else>
      <p v-if="busy && !unit" class="state-note" role="status">Loading this card’s numbered history…</p>
      <p v-if="error" class="state-note error-note" role="status">{{ error }}</p>
      <div v-if="unit" class="checkpoint-panel">
        <div class="checkpoint-meta"><span class="eyebrow">{{ unit.context.complete ? 'Latest numbered checkpoint' : 'Historical checkpoint' }}</span><span class="verification-label" :class="{ verified: unit.context.verified }">{{ unit.context.verified ? 'Verified' : 'Verification pending' }}</span></div>
        <div class="checkpoint-block"><span class="eyebrow">Block</span><strong>{{ Number(unit.context.numbering_tip.height).toLocaleString('en-US') }}</strong></div>
        <div class="checkpoint-location"><span class="eyebrow">Location at this checkpoint</span><p>{{ locationLabel(unit.location) }}</p><p v-if="unit.location.controller && unit.location.kind !== 'address'" class="controller">Associated address: {{ unit.location.controller }}</p></div>
        <p v-if="!unit.context.complete || !unit.context.verified" class="partial-note">Current ownership is not yet verified. This location reflects the checkpoint above.</p>
      </div>
      <CardVault v-if="currentVaultLocation && currentVaultContext" :location="currentVaultLocation" :context="currentVaultContext" />
      <div class="history-heading"><h3>Transaction history</h3><span>{{ movements.length }} loaded</span></div>
      <div v-if="movements.length" class="movement-ledger">
        <div class="ledger-labels"><span>Block</span><span>Movement</span><span>Destination</span><span></span></div>
        <details v-for="row in visibleMovements" :key="`${row.event_index}:${row.leg}`" class="movement-row">
          <summary><span class="movement-block">{{ row.block }}</span><span class="movement-reason">{{ row.reason.replace(/_/g, ' ') }}</span><span class="movement-destination">{{ locationLabel(row.to) }}</span><span class="row-expand" aria-hidden="true">+</span></summary>
          <div class="movement-detail"><p v-if="saleFor(row)" class="sale-price">Recorded sale: {{ priceText(saleFor(row).price, saleFor(row).currency) }} / card · {{ saleFor(row).quantity }} {{ saleFor(row).quantity === 1 ? 'card' : 'cards' }} in this trade</p><dl><div><dt>From</dt><dd>{{ locationLabel(row.from) }}</dd></div><div><dt>To</dt><dd>{{ locationLabel(row.to) }}</dd></div></dl><a v-if="row.tx_hash" class="text-link" :href="`${explorerUrl}/tx/${encodeURIComponent(row.tx_hash)}`" target="_blank" rel="noopener noreferrer">Transaction {{ row.tx_hash.slice(0, 12) }}… ↗</a></div>
        </details>
      </div>
      <p v-else-if="page && !busy" class="state-note">{{ page.has_more ? 'No matching movements in this scanned page. Continue to the next page.' : 'No movements at this checkpoint.' }}</p>
      <div class="provenance-actions"><button v-if="!showAll && movements.length > 6" type="button" class="text-link" @click="showAll = true">Show all {{ movements.length }} loaded ↓</button><button v-else-if="page?.has_more" type="button" class="text-link" :disabled="busy" @click="loadMore">{{ busy ? 'Loading…' : 'Load more history ↓' }}</button><button type="button" class="refresh-card" :disabled="busy" @click="loadCard">Refresh checkpoint ↻</button></div>
    </template>
  </section>
</template>

<script setup>
/* global defineProps */
import { computed, ref, watch, onUnmounted } from 'vue';
import useEspoNumbering, { numberingClient } from '@/composables/useEspoNumbering';
import { canInspect, validSerial, locationLabel } from '@/services/numbering.mjs';
import { espoExplorerUrl } from '@/services/config';
import CardVault from '@/components/CardVault.vue';
import useMarket from '@/composables/useMarket';
import { tradeForMovement, priceText } from '@/services/market.mjs';
const { trades, vaults: liveVaults } = useMarket();
const currentVaultContext = liveVaults.context;
const currentVaultLocation = computed(() => liveVaults.locations.value?.[props.serial]);
const saleFor = row => tradeForMovement(row, trades.value);
const props = defineProps({ serial: { type: String, required: true } });
const { status, error: statusError } = useEspoNumbering();
const explorerUrl = espoExplorerUrl;
const guideUrl = computed(() => `${explorerUrl}/counterparty/numbering?asset=RAREPEPE&serial=${encodeURIComponent(props.serial)}`);
const unit = ref(null);
const movements = ref([]);
const page = ref(null);
const showAll = ref(false);
const visibleMovements = computed(() => showAll.value ? movements.value : movements.value.slice(0, 6));
const busy = ref(false);
const error = ref('');
let requestId = 0;

function explain(cause) {
  if (cause.message.includes('stale_cursor')) return 'A newer history checkpoint is available. Refresh this card to load it.';
  return 'This card’s latest history is unavailable at the moment. Refresh to try again; the saved history is shown below.';
}
async function loadCard() {
  const id = ++requestId;
  showAll.value = false; unit.value = null; movements.value = []; page.value = null; error.value = ''; busy.value = false;
  if (!validSerial(props.serial) || !canInspect(status.value)) return;
  busy.value = true;
  try {
    for (let attempt = 0; attempt < 3; attempt++) {
      const [card, history] = await Promise.all([numberingClient.unit(props.serial), numberingClient.history(props.serial)]);
      if (id !== requestId) return;
      if (card.context.generation !== history.context.generation) {
        if (attempt < 2) continue;
        throw new Error('stale_cursor');
      }
      unit.value = card; page.value = history; movements.value = history.items;
      break;
    }
  } catch (cause) { if (id === requestId) error.value = explain(cause); }
  finally { if (id === requestId) busy.value = false; }
}
async function loadMore() {
  if (busy.value || !page.value?.has_more) return;
  const id = requestId;
  showAll.value = true;
  busy.value = true; error.value = '';
  try {
    const next = await numberingClient.history(props.serial, page.value);
    if (id !== requestId) return;
    movements.value = [...movements.value, ...next.items]; page.value = next;
  } catch (cause) { if (id === requestId) error.value = explain(cause); }
  finally { if (id === requestId) busy.value = false; }
}
watch([() => props.serial, () => canInspect(status.value)], loadCard, { immediate: true });
onUnmounted(() => { requestId += 1; });
</script>

<style scoped>
.sale-price { margin-bottom: 12px; color: var(--forest); font-weight: 600; }
.provenance-heading { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 18px; }
.provenance-heading h2 { font-size: 17px; margin: 0; }
.provenance-heading .text-link { font-size: 10px; }
.checkpoint-panel { background: #d9eeba; box-shadow: var(--pixel-shadow); padding: 22px 24px; }
.checkpoint-meta { display: flex; gap: 12px; justify-content: space-between; align-items: center; }
.checkpoint-meta .eyebrow { font-size: 9px; color: #4b6237; }
.verification-label { font-size: 10px; color: #674c10; background: #fbf3cd; padding: 4px 7px; white-space: nowrap; }
.verification-label.verified { color: var(--forest); background: #c0e996; }
.checkpoint-block { display: flex; align-items: baseline; gap: 13px; margin: 19px 0; }
.checkpoint-block .eyebrow { font-size: 10px; color: #506342; }
.checkpoint-block strong { font: 29px/1 var(--font-titles); color: var(--forest); }
.checkpoint-location { border-top: 1px solid #b2ca98; padding-top: 14px; }
.checkpoint-location .eyebrow { font-size: 9px; color: #506342; }
.checkpoint-location p { font: 13px/1.6 monospace; color: #24441a; overflow-wrap: anywhere; margin-top: 6px; }
.checkpoint-location p.controller { font-size: 11px; }
.partial-note { color: #556445; font-size: 12px; margin-top: 15px; line-height: 1.5; }
.history-heading { display: flex; justify-content: space-between; align-items: center; margin: 30px 0 14px; gap: 12px; }
.history-heading h3 { font-size: 15px; margin: 0; }
.history-heading > span { font-size: 11px; color: var(--muted); }
.movement-ledger { box-shadow: 4px 5px 0 var(--forest); }
.ledger-labels,.movement-row summary { display: grid; grid-template-columns: 70px minmax(110px,1fr) minmax(0,1.3fr) 12px; gap: 12px; align-items: center; padding: 13px 14px; }
.ledger-labels { font: 8px var(--font-titles); background: var(--paper); padding-top: 0; }
.movement-row { background: #b4df81; }
.movement-row:nth-of-type(even) { background: #e5f5ca; }
.movement-row summary { list-style: none; font: 10px/1.5 var(--font-titles); }
.movement-row summary::-webkit-details-marker { display: none; }
.movement-reason { color: #38631e; }
.movement-destination { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font: 11px monospace; }
.row-expand { font: 19px var(--font-primary); color: var(--forest); }
.movement-row[open] .row-expand { transform: rotate(45deg); }
.movement-detail { padding: 4px 14px 17px; font-size: 12px; }
.movement-detail dl { display: grid; gap: 8px; margin: 0 0 10px; }
.movement-detail dl > div { display: grid; grid-template-columns: 42px minmax(0,1fr); gap: 10px; }
.movement-detail dt { font: 9px/1.7 var(--font-titles); color: #536c41; }
.movement-detail dd { font-family: monospace; overflow-wrap: anywhere; margin: 0; }
.movement-detail .text-link { font-size: 9px; }
.provenance-actions { display: flex; gap: 15px; justify-content: space-between; flex-wrap: wrap; margin-top: 21px; }
.provenance-actions button { background: none; border: 0; padding: 3px 0; color: var(--green); }
.provenance-actions .text-link { font-size: 9px; }
.refresh-card { font-size: 12px; margin-left: auto; }
.state-note { background: var(--pale); border-left: 3px solid var(--green); padding: 16px; font-size: 14px; margin-bottom: 16px; }
.error-note { background: #f9efcc; border-color: #aa8e35; }
@media (max-width: 700px) {
  .checkpoint-panel { padding: 18px; }
  .checkpoint-meta { flex-wrap: wrap; gap: 6px; }
  .checkpoint-block strong { font-size: 26px; }
  .ledger-labels,.movement-row summary { grid-template-columns: 54px minmax(95px,1fr) minmax(0,1fr) 10px; gap: 8px; padding: 12px 10px; }
  .movement-row summary { font-size: 9px; }
  .ledger-labels { padding-top: 0; }
  .movement-destination { font-size: 10px; }
  .provenance-heading h2 { font-size: 15px; }
}
</style>
